import { ReactNode } from 'react'
import { Plus, Search } from 'lucide-react'
import { cn } from '../../lib/utils'

export interface SecondaryRailProps {
  icon?: ReactNode
  title: string
  onCreate?: () => void
  createLabel?: string
  search?: { value: string; onChange: (v: string) => void; placeholder?: string }
  children: ReactNode
  footer?: ReactNode
  className?: string
}

export function SecondaryRail({ icon, title, onCreate, createLabel = 'New', search, children, footer, className }: SecondaryRailProps) {
  return (
    <div className={cn('flex h-full w-full flex-col', className)}>
      <div className="flex h-14 shrink-0 items-center gap-2 border-b border-border px-3">
        {icon && <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">{icon}</div>}
        <h2 className="flex-1 truncate text-sm font-semibold text-foreground">{title}</h2>
        {onCreate && (
          <button
            type="button"
            onClick={onCreate}
            title={createLabel}
            aria-label={createLabel}
            className="flex h-7 w-7 items-center justify-center rounded-lg text-muted-foreground hover:bg-accent/60 hover:text-foreground"
          >
            <Plus size={16} />
          </button>
        )}
      </div>
      {search && (
        <div className="shrink-0 border-b border-border p-2.5">
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              value={search.value}
              onChange={(e) => search.onChange(e.target.value)}
              placeholder={search.placeholder ?? 'Search…'}
              aria-label={search.placeholder ?? 'Search'}
              className="w-full rounded-lg bg-muted py-1.5 pl-8 pr-2 text-sm text-foreground outline-none ring-1 ring-border placeholder:text-muted-foreground focus:ring-2 focus:ring-primary/40"
            />
          </div>
        </div>
      )}
      <nav className="flex-1 space-y-4 overflow-y-auto p-2">{children}</nav>
      {footer && <div className="shrink-0 border-t border-border">{footer}</div>}
    </div>
  )
}

export interface RailItemProps {
  active?: boolean
  icon?: ReactNode
  label: ReactNode
  count?: number | string
  onClick?: () => void
  className?: string
}

export function RailItem({ active, icon, label, count, onClick, className }: RailItemProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-sm transition',
        active ? 'bg-primary/10 font-medium text-foreground' : 'text-muted-foreground hover:bg-accent/60 hover:text-foreground',
        className,
      )}
    >
      {icon && <span className="w-4 shrink-0 text-center">{icon}</span>}
      <span className="flex-1 truncate">{label}</span>
      {count != null && <span className="text-[10px] tabular-nums text-muted-foreground/70">{count}</span>}
    </button>
  )
}

export interface RailSectionProps {
  label: ReactNode
  count?: number | string
  icon?: ReactNode
  children: ReactNode
  className?: string
}

export function RailSection({ label, count, icon, children, className }: RailSectionProps) {
  return (
    <div className={className}>
      <div className="mb-1 flex items-center gap-1.5 px-2.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
        {icon && <span className="shrink-0">{icon}</span>}
        <span className="flex-1">{label}</span>
        {count != null && <span className="tabular-nums">{count}</span>}
      </div>
      <div className="space-y-0.5">{children}</div>
    </div>
  )
}
